import { sharePointAttachmentRef } from '@/features/sharepoint/sharepoint-attachment.js';
import type {
  ArtifactAttachmentRef,
  OutgoingAttachment,
  SharePointAttachmentRef,
} from '@/integration/types.js';
import type { QuickActionFile } from '@/lib/api/quick-actions.js';
import type { SharePointSearchHit } from '@/lib/api/sharepoint.js';
import {
  workspaceArtifactContentUrl,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';
import type { ComposerAttachment } from '@/ui/chat/composer/index.js';

/** Mirrors the service's cap, the chat composer's and the artifact owner's ten-reference limit. */
export const MAX_QUICK_ACTION_FILES = 10;

/**
 * A workspace file as a card stores it. No revision, unlike a chat chip: a saved card is run again and
 * again, and each run should read the file as it is then — the way an automation reads its files.
 */
export function quickActionFileFromArtifact(
  workspaceId: string,
  artifact: WorkspaceArtifact,
): QuickActionFile {
  return {
    kind: 'artifact',
    workspace_id: workspaceId,
    artifact_id: artifact.id,
    name: artifact.name,
    content_type: artifact.contentType,
    size: artifact.size >= 0 ? artifact.size : 0,
  };
}

/** A SharePoint search hit as a card stores it — the composer's own address, plus the workspace. */
export function quickActionFileFromSharePoint(
  workspaceId: string,
  hit: SharePointSearchHit,
): QuickActionFile {
  const ref = sharePointAttachmentRef({
    connectionId: null,
    siteId: hit.siteId,
    driveId: hit.driveId,
    item: hit,
  });
  return {
    kind: 'sharepoint',
    workspace_id: workspaceId,
    site_id: ref.site_id,
    drive_id: ref.drive_id,
    item_id: ref.item_id,
    name: ref.name,
    ...(ref.web_url ? { web_url: ref.web_url } : {}),
  };
}

/** What makes two files on a card the same file. */
export function quickActionFileKey(file: QuickActionFile): string {
  return file.kind === 'artifact'
    ? `artifact:${file.workspace_id}:${file.artifact_id}`
    : `sharepoint:${file.workspace_id}:${file.drive_id}:${file.item_id}`;
}

/**
 * The card's files with one more, unless it is already there. `capped` says the file was refused
 * because the card is full, so the caller can say so instead of dropping it in silence.
 */
export function withQuickActionFile(
  files: readonly QuickActionFile[],
  file: QuickActionFile,
): { files: QuickActionFile[]; capped: boolean } {
  const key = quickActionFileKey(file);
  if (files.some((existing) => quickActionFileKey(existing) === key)) {
    return { files: [...files], capped: false };
  }
  if (files.length >= MAX_QUICK_ACTION_FILES) {
    return { files: [...files], capped: true };
  }
  return { files: [...files, file], capped: false };
}

/**
 * The card's files a launch in `workspaceId` can carry, as the send path takes them, and how many it
 * has to leave out. A reference opens only inside the workspace it was picked in — the service builds
 * its address from the conversation's workspace — so a personal card shown in a shared workspace keeps
 * its personal files at home rather than sending references that would not open.
 */
export function launchableFiles(
  files: readonly QuickActionFile[],
  workspaceId: string | null,
): { attachments: OutgoingAttachment[]; skipped: number } {
  const usable = files.filter((file) => file.workspace_id === workspaceId);
  return {
    attachments: usable.map(toOutgoingAttachment),
    skipped: files.length - usable.length,
  };
}

function toOutgoingAttachment(file: QuickActionFile): OutgoingAttachment {
  if (file.kind === 'artifact') {
    const ref: ArtifactAttachmentRef = {
      artifact_id: file.artifact_id,
      name: file.name,
      content_type: file.content_type,
      size: file.size,
      url: workspaceArtifactContentUrl(file.workspace_id, file.artifact_id),
    };
    return ref;
  }
  const ref: SharePointAttachmentRef = {
    site_id: file.site_id,
    drive_id: file.drive_id,
    item_id: file.item_id,
    name: file.name,
    ...(file.web_url ? { web_url: file.web_url } : {}),
  };
  return ref;
}

/** A ready, byte-less composer chip for a reference — the shape `stageLauncherSubmit` takes. */
export function referenceChip(
  attachment: OutgoingAttachment,
): ComposerAttachment {
  if ('artifact_id' in attachment) {
    return {
      id: `artifact:${attachment.artifact_id}`,
      name: attachment.name,
      size: attachment.size,
      mime: attachment.content_type,
      thumbUrl: null,
      uploadState: 'uploaded',
      artifactRef: attachment,
    };
  }
  const sharePoint = attachment as SharePointAttachmentRef;
  return {
    id: `sharepoint:${sharePoint.drive_id}:${sharePoint.item_id}`,
    name: sharePoint.name,
    size: 0,
    mime: 'application/octet-stream',
    thumbUrl: null,
    uploadState: 'uploaded',
    sharePointRef: sharePoint,
  };
}
